import {
	Container,
	CTA,
	Spinner,
	Text,
	TextAlignment,
	TextInput,
	TokenTextAppearance,
} from '@volkswagen-onehub/components-core';
import { Checkmark, ChevronDown, Info, Locate, Magnifier } from '@volkswagen-onehub/icons-core';
import React, { useEffect, useMemo, useState } from 'react';
import { DealerOptionCard } from './dealer-option-card';
import { NdpsDealer } from '@esportal/portal-service';
import { DealersService } from '../../../fetch-bff/get-dealers';
import { CardsGroup } from '../screen/cards-group';

// Número de dealers por página - fácil de ajustar
const DEALERS_PER_PAGE = 6;

export const DealersRadio: React.FC<{ onDealerSelect?: (dealer: NdpsDealer | null) => void }> = ({
	onDealerSelect,
}) => {
	const [searchValue, setSearchValue] = useState('');
	const [allDealers, setAllDealers] = useState<NdpsDealer[]>([]); // Todos los dealers obtenidos del servicio
	const [dealers, setDealers] = useState<NdpsDealer[]>([]); // Dealers mostrados actualmente
	const [isTyping, setIsTyping] = useState(false);
	const [showDealers, setShowDealers] = useState(false);
	const [searchedDealerKey, setSearchedDealerKey] = useState<string | null>(null);
	const [showOnlySelected, setShowOnlySelected] = useState(false);
	const [locationUsed, setLocationUsed] = useState(false);
	const [currentIndex, setCurrentIndex] = useState(DEALERS_PER_PAGE); // Índice para paginación
	const [showedOutOfLocationText, setShowedOutOfLocationText] = useState(false);
	const [suggestions, setSuggestions] = useState<string[]>([]);
	const [suggestedSearch, setSuggestedSearch] = useState(false);

	const dealersService = useMemo(() => new DealersService(), []);

	// Limpia el estado cuando se borra el valor de búsqueda
	useEffect(() => {
		if (!searchValue.trim()) {
			setAllDealers([]);
			setDealers([]);
			setIsTyping(false);
			setShowDealers(false);
		}
	}, [searchValue]);

	// Ejecuta la búsqueda de dealers cuando se selecciona una sugerencia
	useEffect(() => {
		if (!suggestedSearch || !searchValue.trim()) {
			return;
		}

		setIsTyping(true);
		setShowedOutOfLocationText(false);
		setShowOnlySelected(false);
		const timeoutId = setTimeout(async () => {
			try {
				const results = await dealersService.getSortedDealers(searchValue);
				setAllDealers(results);
				// Si el primer dealer tiene Distance, todos tienen Distance -> mostrar 6 y el label
				if (results[0]?.Distance !== undefined) {
					setDealers(results.slice(0, DEALERS_PER_PAGE));
					setCurrentIndex(DEALERS_PER_PAGE);
					setShowedOutOfLocationText(true); // Mostrar label inmediatamente
				} else {
					// Mostrar todos los dealers SIN Distance hasta encontrar el primero CON Distance
					const firstWithDistanceIndex = results.findIndex((d) => d.Distance !== undefined);
					const initialCount = firstWithDistanceIndex === -1 ? results.length : firstWithDistanceIndex;
					setDealers(results.slice(0, initialCount));
					setCurrentIndex(initialCount);
					setShowedOutOfLocationText(false); // No mostrar label inicialmente
				}
				setShowDealers(true);
				setIsTyping(false);
				setSuggestedSearch(false);
			} catch (error) {
				console.error('Error loading dealers:', error);
				setAllDealers([]);
				setDealers([]);
				setShowDealers(false);
				setIsTyping(false);
				setSuggestedSearch(false);
			}
		}, 500);

		return () => clearTimeout(timeoutId);
	}, [suggestedSearch, dealersService]);

	// Manejo del input de búsqueda
	const handleChange = async (e: any) => {
		setLocationUsed(false);
		const value = e.target.value;
		setSearchValue(value);

		// Limpiar dealers cuando se edita el input
		setAllDealers([]);
		setDealers([]);
		setShowDealers(false);
		setSearchedDealerKey(null);
		setShowOnlySelected(false);
		updateDealerField(null);

		// Mostrar sugerencias si es numérico con 4+ dígitos o texto con 3+ caracteres
		const isNumeric = /^\d+$/.test(value);
		const shouldShowSuggestions = (isNumeric && value.length >= 4) || (!isNumeric && value.length >= 3);

		if (shouldShowSuggestions) {
			const suggestions = await dealersService.getSuggestions(value);
			setSuggestions(suggestions);
		} else {
			setSuggestions([]);
		}
	};

	// Precarga de dealers
	const handleFocus = () => {
		dealersService.fetchDealers();
	};

	// Selección de sugerencia
	const handleSuggestionClick = (suggestion: string) => {
		setSearchValue(suggestion);
		setSuggestions([]);
		setSuggestedSearch(true);
	};

	// Cargar más dealers
	const handleMoreDealers = () => {
		const firstDealerHasDistance = allDealers[0]?.Distance !== undefined;

		if (firstDealerHasDistance) {
			// Si ya tienen Distance desde el inicio, no cambiar el estado del label
			const nextDealers = allDealers.slice(currentIndex, currentIndex + DEALERS_PER_PAGE);
			setDealers((prev) => [...prev, ...nextDealers]);
			setCurrentIndex((prev) => prev + DEALERS_PER_PAGE);
		} else {
			// Solo mostrar el label la primera vez que se amplía la búsqueda
			if (!showedOutOfLocationText && !locationUsed) {
				setShowedOutOfLocationText(true);
			}
			const nextDealers = allDealers.slice(currentIndex, currentIndex + DEALERS_PER_PAGE);
			setDealers((prev) => [...prev, ...nextDealers]);
			setCurrentIndex((prev) => prev + DEALERS_PER_PAGE);
		}
	};

	// Actualizar dealer seleccionado
	const updateDealerField = (dealer: NdpsDealer | null) => {
		onDealerSelect?.(dealer);
	};

	const handleRestoreAllResults = () => {
		setShowOnlySelected(false);
	};

	const handleDealerSelect = (dealerKey: string) => {
		const dealer = allDealers.find((d) => `${d.Id}` === dealerKey);
		setSearchedDealerKey(dealerKey);
		setShowOnlySelected(true);
		updateDealerField(dealer ?? null);
	};

	const handleUseLocation = () => {
		if (!navigator.geolocation) {
			console.error('Geolocalización no soportada');
			return;
		}

		setSearchValue('');
		setIsTyping(true);
		setShowOnlySelected(false);
		setSuggestions([]);
		navigator.geolocation.getCurrentPosition(
			async (position) => {
				try {
					const { latitude, longitude } = position.coords;
					const results = await dealersService.getSortedDealersByCoordinates(latitude, longitude);
					setAllDealers(results);
					setShowedOutOfLocationText(false);
					// Por ubicación siempre mostramos 6 (todos tienen Distance)
					setDealers(results.slice(0, DEALERS_PER_PAGE));
					setCurrentIndex(DEALERS_PER_PAGE);
					setShowDealers(true);
					setIsTyping(false);
					setLocationUsed(true);
				} catch (error) {
					console.error('Error loading dealers by location:', error);
					setAllDealers([]);
					setDealers([]);
					setShowDealers(false);
					setIsTyping(false);
				}
			},
			(error) => {
				console.error('Error obteniendo ubicación:', error);
				setIsTyping(false);
			},
		);
	};

	const filteredDealers =
		showOnlySelected && searchedDealerKey ? dealers.filter((d) => `${d.Id}` === searchedDealerKey) : dealers;

	const dealersWithoutDistance = filteredDealers.filter(
		(d) => !(d as any).isOutOfLocationText && d.Distance === undefined,
	);
	const dealersWithDistance = filteredDealers.filter(
		(d) => !(d as any).isOutOfLocationText && d.Distance !== undefined,
	);

	return (
		<>
			<div
				className="dealers-input-container"
				style={{
					display: 'flex',
					alignItems: 'flex-start',
					justifyContent: 'center',
					gap: '20px',
					marginBottom: '40px',
					flexDirection: 'row',
				}}
			>
				<style>{`
					@media (max-width: 599px) {
						.dealers-input-container {
							flex-direction: column !important;
							align-items: stretch !important;
						}
						.dealers-button-container {
							align-self: flex-start !important;
						}
					}
				`}</style>
				<div style={{ minWidth: '300px', flex: 1, maxWidth: '400px' }}>
					<TextInput
						name="searchedDealer"
						required
						value={searchValue}
						onChange={handleChange}
						onFocus={handleFocus}
						label="Código postal o localidad"
						icon={<Magnifier variant="default" aria-hidden />}
						maxLength={/^\d+$/.test(searchValue) ? 5 : undefined}
					/>
					{suggestions.length > 0 && !showDealers && (
						<div>
							{suggestions.map((suggestion, index) => (
								<div
									key={index}
									style={{
										padding: '8px 12px',
										cursor: 'pointer',
										borderBottom: '1px solid #c2cacf',
										borderLeft: '1px solid #c2cacf',
										borderRight: '1px solid #c2cacf',
										backgroundColor: '#fff',
									}}
									onClick={() => handleSuggestionClick(suggestion)}
								>
									<Text>{suggestion}</Text>
								</div>
							))}
						</div>
					)}
				</div>
				<div className="dealers-button-container" style={{ minWidth: '150px', marginTop: '8px' }}>
					<CTA
						tag="button"
						emphasis={locationUsed ? 'primary' : 'secondary'}
						size="small"
						icon={locationUsed ? <Checkmark variant="small" aria-hidden /> : <Locate variant="small" aria-hidden />}
						onClick={handleUseLocation}
					>
						Usar mi ubicación
					</CTA>
				</div>
			</div>

			{isTyping ? (
				<Container horizontalAlign={'center'}>
					<Spinner variant="large" />
				</Container>
			) : (
				showDealers &&
				(dealers.length > 0 ? (
					<>
						{dealersWithoutDistance.length > 0 && (
							<CardsGroup marginBottom="20px">
								{dealersWithoutDistance.map((dealer) => {
									const dealerKey = `${dealer.Id}`;
									return (
										<DealerOptionCard
											key={dealerKey}
											dealer={dealer}
											isActive={searchedDealerKey === dealerKey}
											onSelect={() => handleDealerSelect(dealerKey)}
										/>
									);
								})}
							</CardsGroup>
						)}
						{showedOutOfLocationText && !showOnlySelected && (
							<div
								style={{
									marginBottom: '20px',
									display: 'flex',
									gap: '10px',
									color: '#5b08a4',
								}}
							>
								<Info aria-hidden />{' '}
								<Text bold appearance={TokenTextAppearance.copy200}>
									<span style={{ color: '#5b08a4' }}>
										{/^\d+$/.test(searchValue) ? 'Fuera de tu código postal' : 'Fuera de tu localidad'}
									</span>
								</Text>
							</div>
						)}
						{dealersWithDistance.length > 0 && (
							<CardsGroup marginBottom="20px">
								{dealersWithDistance.map((dealer) => {
									const dealerKey = `${dealer.Id}`;
									return (
										<DealerOptionCard
											key={dealerKey}
											dealer={dealer}
											isActive={searchedDealerKey === dealerKey}
											onSelect={() => handleDealerSelect(dealerKey)}
										/>
									);
								})}
							</CardsGroup>
						)}
						{showOnlySelected ? (
							<Text textAlign={TextAlignment.center}>
								<CTA
									tag="button"
									emphasis="secondary"
									size="small"
									icon={<ChevronDown variant="small" aria-hidden />}
									onClick={handleRestoreAllResults}
								>
									Ver todos los resultados
								</CTA>
							</Text>
						) : (
							currentIndex < allDealers.length && (
								<Text textAlign={TextAlignment.center}>
									<CTA
										tag="button"
										emphasis="secondary"
										size="small"
										icon={<ChevronDown variant="small" aria-hidden />}
										iconPosition="end"
										onClick={handleMoreDealers}
									>
										Ampliar búsqueda
									</CTA>
								</Text>
							)
						)}
					</>
				) : (
					<Text>No hay resultados</Text>
				))
			)}
		</>
	);
};
